import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { portalApi, recordApi } from '../api';
import { Statuses } from '../constants/statuses';
import { activitySearch } from '../redux/modules/activityLog';
import { createErrorToast, createSuccessToast, createToast } from '../redux/modules/app';
import { queryClient } from '../utils/queryClientUtil';

import insuredKeys from './models/insured/keys';
import policyKeys from './models/policy/keys';
import recordKeys from './models/record/keys';
import {
  camelCaseApiResponse, getPageData,
  modifyInfiniteListsItem, optimisticUpdateActiveRecords,
  optimisticUpdateDocumentStatuses,
  updatePolicyRequirementsWithLoadingStatus
} from './util';

/* QUERIES
========================================================= */
export const useRecordData = (recordId, options = {}) => useQuery(
  recordKeys.record(parseInt(recordId, 10)),
  () => camelCaseApiResponse(() => recordApi.getRecord(recordId)),
  options
);

export const useActiveRecordPolicies = (recordId, options = {}) => useQuery(
  recordKeys.activeRecordPolicies(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordPolicies(recordId, { active: true })),
  options
);

export const COMPANY_ACTIVE_RECORDS_SEARCH_FIELDS = [
  'insured.company_name',
  'job.name',
  'job.number',
  'insurance_requirement.contract_number'
];

/**
 * Elasticsearch body for the insurance_requirements index: one row per active (non-archived),
 * insured-parented record in the company. Item-parented requirements share the index and are
 * excluded so the list reads as records.
 */
export const buildCompanyActiveRecordsSearchParams = ({
  companyId,
  searchTerm = '',
  excludeRecordId = null,
  excludeInsuredId = null
}) => {
  const term = searchTerm?.trim() ?? '';
  const mustNot = [];

  if (excludeRecordId) {
    mustNot.push({ term: { 'insurance_requirement.id': parseInt(excludeRecordId, 10) } });
  }

  if (excludeInsuredId) {
    mustNot.push({ term: { 'insured.id': parseInt(excludeInsuredId, 10) } });
  }

  return {
    query: {
      bool: {
        filter: [
          { term: { 'company.id': parseInt(companyId, 10) } },
          { term: { 'insurance_requirement.parent_type.keyword': 'insured' } },
          { terms: { 'status.keyword': [Statuses.Approved, Statuses.NotApproved] } }
        ],
        must_not: mustNot,
        must: term
          ? [{
            multi_match: {
              query: term,
              fields: COMPANY_ACTIVE_RECORDS_SEARCH_FIELDS,
              type: 'bool_prefix',
              operator: 'and'
            }
          }]
          : []
      }
    },
    sort: [
      { 'insured.company_name.keyword': { order: 'asc', unmapped_type: 'keyword' } },
      { 'job.name.keyword': { order: 'asc', unmapped_type: 'keyword' } },
      { 'insurance_requirement.id': { order: 'asc' } }
    ]
  };
};

export const useCompanyActiveRecordsSearch = ({
  companyId,
  searchTerm = '',
  excludeRecordId = null,
  excludeInsuredId = null,
  pageSize = 25,
  options = {}
}) => useInfiniteQuery(
  recordKeys.companyActiveRecordsSearch(companyId, { searchTerm, excludeRecordId, excludeInsuredId }),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => portalApi.searchInsuranceRequirements(
    buildCompanyActiveRecordsSearchParams({ companyId, searchTerm, excludeRecordId, excludeInsuredId }),
    { page: pageParam, pageSize }
  )),
  {
    enabled: !!companyId,
    keepPreviousData: true,
    ...options,
    getNextPageParam: getPageData
  }
);

export const RECORD_CLONE_BATCH_POLL_MS = 1500;

export const recordCloneBatchRefetchInterval = data => (data?.finished ? false : RECORD_CLONE_BATCH_POLL_MS);

export const useRecordCloneBatch = (batchId, options = {}) => useQuery(
  recordKeys.recordCloneBatch(batchId),
  () => camelCaseApiResponse(() => recordApi.getRecordCloneBatch(batchId)),
  {
    enabled: !!batchId,
    refetchInterval: recordCloneBatchRefetchInterval,
    refetchOnMount: true,
    ...options
  }
);

/**
 * Drop every cache a finished clone batch may have changed. Target records are removed outright so
 * they refetch on next view. The insured being viewed is invalidated so its mounted queries refetch
 * now; every other insured's cached queries are removed because refetchOnMount is off globally and
 * a stale cache would otherwise survive navigation.
 */
export const flushRecordCloneBatchCaches = ({ recordIds = [], insuredId = null }) => {
  recordIds.forEach(id => queryClient.removeQueries(recordKeys.record(id)));

  queryClient.removeQueries({ queryKey: insuredKeys.all, inactive: true });

  if (insuredId) {
    return Promise.all([
      queryClient.invalidateQueries(insuredKeys.insuredActiveRecords(insuredId)),
      queryClient.invalidateQueries(insuredKeys.insuredOverallStatus(insuredId))
    ]);
  }

  return Promise.resolve();
};

export const useArchivedRecordPolicies = (recordId, options = {}) => useQuery(
  recordKeys.archivedRecordPolicies(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordPolicies(recordId, { archived: true })),
  options
);

export const useActiveRecordDocuments = ({
  recordId,
  options = {}
}) => useQuery(
  recordKeys.activeRecordDocuments(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordDocuments(recordId, { active: true })),
  options
);

export const useArchivedRecordDocuments = ({
  recordId,
  options = {}
}) => useQuery(
  recordKeys.archivedRecordDocuments(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordDocuments(recordId, { archived: true })),
  options
);

export const useRecordPolicyRequirements = (recordId, options = {}) => useQuery(
  recordKeys.recordPolicyRequirements(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordPolicyRequirements(recordId)),
  options
);

export const useRecordItemRequirements = (recordId, options = {}) => useQuery(
  recordKeys.recordItemRequirements(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordItemRequirements(recordId)),
  options
);

export const useRecordDocumentRequirements = (recordId, options = {}) => useQuery(
  recordKeys.recordDocumentRequirements(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordDocumentRequirements(recordId)),
  options
);

export const useRecordLanguageRequirements = (recordId, options = {}) => useQuery(
  recordKeys.recordLanguageRequirements(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordLanguageRequirements(recordId)),
  options
);

export const useRecordQuestionRequirements = (recordId, options = {}) => useQuery(
  recordKeys.recordQuestionRequirements(recordId),
  () => camelCaseApiResponse(() => recordApi.getRecordQuestionRequirements(recordId)),
  options
);

export const useRecordAttachments = (recordId, options = {}) => useQuery(
  recordKeys.recordAttachments(recordId),
  () => camelCaseApiResponse(() => recordApi.listRecordAttachments(recordId)),
  options
);

export const useRecordWatchers = (recordId, options = {}) => useQuery(
  recordKeys.recordWatchers(recordId),
  () => camelCaseApiResponse(() => recordApi.listRecordWatchers(recordId)),
  options
);

export const useRecordGptRequests = ({
  recordId,
  pageSize = 25,
  options = {}
}) => useInfiniteQuery(
  recordKeys.recordGptRequests(recordId),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => recordApi.listRecordGptRequests(
    recordId,
    { page: pageParam, pageSize }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const useGptRequest = ({
  recordId,
  gptRequestId,
  options = {}
}) => useQuery(
  recordKeys.recordGptRequest(recordId, gptRequestId),
  () => camelCaseApiResponse(() => recordApi.getRecordGptRequest(recordId, gptRequestId)),
  options
);

/**
 * Preview of what removing an agent from a record would do. Drives the Remove Agent confirmation
 * copy, so it must never be served from cache: the app sets `refetchOnMount: false` globally, which
 * means a remounted modal would otherwise reuse whatever was cached the last time it opened, even
 * after the underlying contact was edited. `cacheTime: 0` drops the entry as soon as the modal
 * unmounts and `refetchOnMount: 'always'` refetches regardless of staleness, so every open shows
 * the loading state and then current data rather than a stale answer the user might confirm.
 */
export const useAgentRemovalPreview = (recordId, agentUserId, options = {}) => useQuery(
  recordKeys.recordAgentRemovalPreview(recordId, agentUserId),
  () => camelCaseApiResponse(() => recordApi.getAgentRemovalPreview(recordId, agentUserId)),
  {
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    // Once the agent is removed this endpoint legitimately 404s, so retrying is pure noise.
    retry: false,
    ...options
  }
);

/* MUTATIONS
========================================================= */
export const useUpdateRecordRequirements = ({ recordId, insuredId, options }) => {
  const dispatch = useDispatch();
  const activeRecordPoliciesKey = recordKeys.activeRecordPolicies(recordId);
  const activeRecordDocumentsKey = recordKeys.activeRecordDocuments(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const policyRequirements = recordKeys.recordPolicyRequirements(recordId);
  const documentRequirements = recordKeys.recordDocumentRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  let invalidate = false;

  return useMutation(
    ({
      policyId,
      policyTypeId,
      documentTypeId,
      status
    }) => camelCaseApiResponse(() => recordApi.updateRequirementStatuses({
      recordId,
      policyId,
      policyTypeId,
      documentTypeId,
      status
    })),
    {
      onMutate: async updateData => {
        await queryClient.cancelQueries(activeRecordPoliciesKey);
        await queryClient.cancelQueries(activeRecordDocumentsKey);
        await queryClient.cancelQueries(policyRequirements);
        await queryClient.cancelQueries(itemRequirements);
        await queryClient.cancelQueries(documentRequirements);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousActivePolicies = queryClient.getQueryData(activeRecordPoliciesKey);
        const previousActiveDocuments = queryClient.getQueryData(activeRecordDocumentsKey);
        const previousPolicyRequirements = queryClient.getQueryData(policyRequirements);
        const previousItemRequirements = queryClient.getQueryData(itemRequirements);
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirements);
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousActiveRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        if (updateData?.policyId || updateData?.policyTypeId) {
          invalidate = updatePolicyRequirementsWithLoadingStatus({
            policiesKey: activeRecordPoliciesKey,
            policyRequirementsKey: policyRequirements,
            itemRequirementsKey: itemRequirements,
            previousPolicies: previousActivePolicies,
            previousPolicyRequirements,
            previousItemRequirements,
            status: updateData.status,
            policyId: updateData?.policyId,
            policyTypeId: updateData?.policyTypeId
          });
        }

        if (updateData?.documentTypeId) {
          invalidate = optimisticUpdateDocumentStatuses({
            documentRequirementsKey: documentRequirements,
            previousDocumentRequirements,
            documentsKey: activeRecordDocumentsKey,
            previousDocuments: previousActiveDocuments,
            docTypeId: updateData.documentTypeId,
            newStatus: updateData.status
          });
        }

        if (invalidate) {
          dispatch(createToast({ title: 'Updating status...', duration: 60000 }));

          // SET LOADING STATE FOR OVERALL STATUS
          queryClient.setQueryData(overallStatus, oldData => ({ ...oldData, isLoading: true }));

          // SET LOADING STATE FOR ACTIVE RECORD STATUSES
          modifyInfiniteListsItem(
            [activeRecordStatuses],
            'id',
            recordId,
            record => ({ ...record, isLoading: true })
          );
        } else {
          // OPTIMISTIC UPDATE ACTIVE RECORD STATUSES
          optimisticUpdateActiveRecords({
            recordId,
            activeRecordsKey: activeRecordStatuses,
            polReqKey: policyRequirements,
            docReqKey: documentRequirements,
            itemReqKey: itemRequirements
          });
        }

        return {
          previousActiveRecordStatuses,
          previousActivePolicies,
          previousActiveDocuments,
          previousPolicyRequirements,
          previousItemRequirements,
          previousDocumentRequirements,
          previousOverallStatus
        };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Requirement status was not updated successfully.'
        }));

        queryClient.setQueryData(activeRecordPoliciesKey, context.previousActivePolicies);
        queryClient.setQueryData(activeRecordDocumentsKey, context.previousActiveDocuments);
        queryClient.setQueryData(itemRequirements, context.previousItemRequirements);
        queryClient.setQueryData(policyRequirements, context.previousPolicyRequirements);
        queryClient.setQueryData(documentRequirements, context.previousDocumentRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousActiveRecordStatuses);
      },
      onSuccess: async () => {
        if (invalidate) {
          await Promise.all([
            queryClient.invalidateQueries(activeRecordPoliciesKey),
            queryClient.invalidateQueries(activeRecordDocumentsKey),
            queryClient.invalidateQueries(policyRequirements),
            queryClient.invalidateQueries(documentRequirements),
            queryClient.invalidateQueries(overallStatus),
            queryClient.invalidateQueries(activeRecordStatuses)
          ]);

          dispatch(createSuccessToast({ title: 'Status Updated ' }));
        }
      },
      ...options
    }
  );
};

export const useCreateRecordWatcher = ({ recordId, insuredId, options = {} }) => {
  const dispatch = useDispatch();
  const activeRecordsKey = insuredKeys.insuredActiveRecords(insuredId);
  const recordWatchersKey = recordKeys.recordWatchers(recordId);

  return useMutation(
    user => camelCaseApiResponse(() => recordApi.createRecordWatcher(recordId, [user.userId])),
    {
      onMutate: async user => {
        await queryClient.cancelQueries(activeRecordsKey);
        await queryClient.cancelQueries(recordWatchersKey);

        const previousActiveRecords = queryClient.getQueryData(activeRecordsKey);
        const previousRecordWatchers = queryClient.getQueryData(recordWatchersKey);

        dispatch(createSuccessToast({ title: 'Added as record watcher' }));

        // update active records
        modifyInfiniteListsItem(
          [activeRecordsKey],
          'id',
          recordId,
          rec => {
            const recWatcher = {
              resourceType: 'insurance_requirement',
              resourceId: recordId,
              userId: user.userId,
              user
            };

            return { ...rec, watchers: [...rec.watchers, recWatcher] };
          }
        );

        // update record watchers
        queryClient.setQueryData(recordWatchersKey, previousData => {
          const newWatcher = {
            resourceType: 'insurance_requirement',
            resourceId: recordId,
            userId: user.userId,
            user,
            id: uuidv4()
          };

          return [...previousData, newWatcher];
        });

        return { previousActiveRecords, previousRecordWatchers };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Record watcher was not updated successfully.'
        }));

        queryClient.setQueryData(activeRecordsKey, context.previousActiveRecords);
        queryClient.setQueryData(recordWatchersKey, context.previousRecordWatchers);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(activeRecordsKey);
        await queryClient.invalidateQueries(recordWatchersKey);
      },
      ...options
    }
  );
};

export const useDeleteRecordWatcher = ({ recordId, insuredId, options = {} }) => {
  const dispatch = useDispatch();
  const activeRecordsKey = insuredKeys.insuredActiveRecords(insuredId);
  const recordWatchersKey = recordKeys.recordWatchers(recordId);

  return useMutation(
    user => camelCaseApiResponse(() => recordApi.deleteRecordWatcher(recordId, [user.userId])),
    {
      onMutate: async user => {
        await queryClient.cancelQueries(activeRecordsKey);
        await queryClient.cancelQueries(recordWatchersKey);

        const previousActiveRecords = queryClient.getQueryData(activeRecordsKey);
        const previousRecordWatchers = queryClient.getQueryData(recordWatchersKey);

        dispatch(createSuccessToast({ title: 'Deleted record watcher' }));

        modifyInfiniteListsItem(
          [activeRecordsKey],
          'id',
          recordId,
          rec => {
            const filteredRecs = rec.watchers.filter(w => w.userId !== user.userId);

            return { ...rec, watchers: filteredRecs };
          }
        );

        queryClient.setQueryData(recordWatchersKey, previousQuery => previousQuery.filter(w => w.userId !== user.userId));

        return { previousActiveRecords, previousRecordWatchers };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Record watcher was not updated successfully.'
        }));

        queryClient.setQueryData(activeRecordsKey, context.previousActiveRecords);
        queryClient.setQueryData(recordWatchersKey, context.previousRecordWatchers);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(activeRecordsKey);
        await queryClient.invalidateQueries(recordWatchersKey);
      },
      ...options
    }
  );
};

export const useTransferRecordProject = ({ recordId, insuredId, options = {} }) => {
  const dispatch = useDispatch();
  const policies = recordKeys.recordPolicies(recordId);
  const documents = recordKeys.recordDocuments(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const policyRequirements = recordKeys.recordPolicyRequirements(recordId);
  const documentRequirements = recordKeys.recordDocumentRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);

  return useMutation(
    jobId => camelCaseApiResponse(() => recordApi.transferRecordProject(recordId, jobId)),
    {
      onMutate: async () => {
        dispatch(createToast({
          title: 'Transferring record',
          description: 'This may take a second...',
          duration: 60000
        }));

        return {};
      },
      onError: (error, _insured, _context) => {
        if (error.status === 422) {
          dispatch(createToast({
            title: 'Oops! Looks like this insured already has an existing record for the selected project',
            description: 'Your record was not transferred.',
            status: 'warning',
            duration: 10000
          }));

          return false;
        }

        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your record was not transferred successfully.',
          error
        }));
      },
      onSuccess: async () => {
        dispatch(createSuccessToast({ title: 'Record transferred successfully!' }));

        Promise.all([
          queryClient.invalidateQueries(policies),
          queryClient.invalidateQueries(documents),
          queryClient.invalidateQueries(itemRequirements),
          queryClient.invalidateQueries(policyRequirements),
          queryClient.invalidateQueries(documentRequirements),
          queryClient.invalidateQueries(insuredKeys.insuredAllRecordQueriesKey(insuredId)),
          queryClient.invalidateQueries(insuredKeys.insuredAllRecordsWithTrashed(insuredId)),
          queryClient.invalidateQueries(overallStatus)
        ]);
      },
      ...options
    }
  );
};

export const useRemoveRecordAgent = ({ recordId, insuredId, options = {} }) => {
  const dispatch = useDispatch();
  const activePoliciesKey = recordKeys.activeRecordPolicies(recordId);
  const archivedPoliciesKey = recordKeys.archivedRecordPolicies(recordId);
  const recordKey = recordKeys.record(recordId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  // Removal reassigns every policy the agent owns on the record, not just the one whose menu
  // launched it, and we don't know those policy ids here — so drop every cached policy-agent
  // lookup rather than one.
  const isPolicyAgentQuery = q => q.queryKey[0] === policyKeys.all[0]
    && q.queryKey[q.queryKey.length - 1] === policyKeys.policyAgentSegment;

  return useMutation(
    agentUserId => camelCaseApiResponse(() => recordApi.removeRecordAgent(recordId, agentUserId)),
    {
      mutationKey: recordKeys.removeRecordAgentMutation(recordId),
      onError: error => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'The agent was not removed successfully.',
          error
        }));
      },
      onSuccess: async () => {
        dispatch(createSuccessToast({ title: 'Agent removed successfully!' }));

        await Promise.all([
          queryClient.invalidateQueries({ predicate: isPolicyAgentQuery }),
          queryClient.invalidateQueries(activePoliciesKey),
          queryClient.invalidateQueries(archivedPoliciesKey),
          queryClient.invalidateQueries(recordKey),
          queryClient.invalidateQueries(insuredKeys.insured(insuredId)),
          queryClient.invalidateQueries(activeRecordStatuses)
        ]);

        // Activity Log is still backed by the legacy redux search blueprint (not react-query) —
        // refresh it so the new "agent removed" activity entry shows up without a page reload.
        dispatch(activitySearch.getPage());
      },
      ...options
    }
  );
};

export const useAiScanDocs = ({ recordId, options = {} }) => {
  const dispatch = useDispatch();

  return useMutation(
    ({ docIds }) => camelCaseApiResponse(() => portalApi.aiScanDocuments({ recordId, docIds, sync: false, publishAnswers: true })),
    {
      onMutate: async () => {
        dispatch(createSuccessToast({
          title: 'Scan initialized',
          description: 'This may take a minute...'
        }));
      },
      ...options
    }
  );
};

export const useCreateRecordCloneBatch = (options = {}) => useMutation(
  ({ recordId, recordIds, policyIds, documentIds, copyRequirementStatuses = false }) => camelCaseApiResponse(
    () => recordApi.createRecordCloneBatch(recordId, { recordIds, policyIds, documentIds, copyRequirementStatuses })
  ),
  options
);

